// src/pages/TaskDetails.jsx
// V15 ENTERPRISE — Airbnb / Zillow / QuintoAndar level
// =====================================================
// 🔥 V15: DataContext como fuente única de verdad
// 🔥 V15: Todas las lecturas usan DataContext.collections.*.findById()
// 🔥 V15: handleComplete → DataContext.collections.tasks.update()
// 🔥 V15: handleDelete → DataContext.collections.tasks.delete()
// 🔥 V15: SyncManager V14 integrado para offline/online ultra robusto
// 🔥 V15: Fallback a database.js solo si DataContext no está disponible
// =====================================================
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Flag,
  User,
  Home,
  Tag,
  Paperclip,
  Edit3,
  Trash2,
  Share2,
  MoreVertical,
  ChevronRight,
  Loader2,
  Sparkles,
  FileText,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Award,
  Zap,
  Target,
  Clock as ClockIcon,
  Check,
  X,
  Download,
} from "lucide-react";
import { useAuth } from "../contexts/useAuth";
import { useNotifications } from "../contexts/NotificationsContext";
import { useIsMobile } from "../hooks/useMediaQuery";
import NativeSheet from "../components/ui/NativeSheet";
import { getTaskShareUrl } from "../config/urls";
import PropertyImage from '../components/PropertyImage';

// 🔥 V15: Importar DataContext como fuente única de verdad
import DataContext from "../lib/data/context/DataContext";

// ======================================================
// 🎯 COMPONENTE PRINCIPAL - TASK DETAILS ULTRA PREMIUM
// ======================================================
export default function TaskDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { database, company, user } = useAuth();
  const { showToast } = useNotifications();
  const isMobile = useIsMobile();

  // 🔥 V15: Obtener userId para DataContext
  const authUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      return stored ? JSON.parse(stored).id : user?.uid || null;
    } catch { return user?.uid || null; }
  }, [user?.uid]);

  // 🔥 V15: Instancia de DataContext como fuente única de verdad
  const ctxRef = useRef(null);
  const getContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    if (authUserId) {
      ctxRef.current = DataContext.getInstance(authUserId);
      return ctxRef.current;
    }
    return null;
  }, [authUserId]);

  // ================================
  // 🎨 WHITE LABEL - Cores
  // ================================
  const primaryColor = useMemo(() => 
    company?.cor || getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-color').trim() || '#2563eb',
    [company?.cor]
  );

  // ================================
  // 📊 ESTADOS
  // ================================
  const [task, setTask] = useState(null);
  const [client, setClient] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // ================================
  // 📅 FORMATADORES
  // ================================
  const formatDate = useCallback((dateString, format = 'full') => {
    if (!dateString) return '—';
    
    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      today.setHours(0, 0, 0, 0);
      tomorrow.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      
      if (format === 'relative') {
        if (date.getTime() === today.getTime()) return 'Hoje';
        if (date.getTime() === tomorrow.getTime()) return 'Amanhã';
        if (date < today) return 'Atrasada';
      }
      
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: format === 'full' ? '2-digit' : undefined,
        minute: format === 'full' ? '2-digit' : undefined
      });
    } catch {
      return dateString;
    }
  }, []);

  // ================================
  // 🎨 HELPERS VISUAIS
  // ================================
  const getStatusConfig = useCallback((status) => {
    const configs = {
      pending: {
        label: 'Pendente',
        color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
        icon: Clock,
        bgLight: 'bg-yellow-50',
        textDark: 'text-yellow-800',
        border: 'border-yellow-200'
      },
      in_progress: {
        label: 'Em Progresso',
        color: 'bg-blue-50 text-blue-700 border-blue-200',
        icon: Flag,
        bgLight: 'bg-blue-50',
        textDark: 'text-blue-800',
        border: 'border-blue-200'
      },
      done: {
        label: 'Concluída',
        color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        icon: CheckCircle2,
        bgLight: 'bg-emerald-50',
        textDark: 'text-emerald-800',
        border: 'border-emerald-200'
      }
    };
    return configs[status] || configs.pending;
  }, []);

  const getPriorityConfig = useCallback((priority) => {
    const configs = {
      low: {
        label: 'Baixa',
        color: 'bg-muted text-secondary border-color',
        icon: Flag,
        badge: 'bg-muted text-slate-800'
      },
      medium: {
        label: 'Média',
        color: 'bg-orange-50 text-orange-700 border-orange-200',
        icon: Flag,
        badge: 'bg-orange-200 text-orange-800'
      },
      high: {
        label: 'Alta',
        color: 'bg-rose-50 text-rose-700 border-rose-200',
        icon: Flag,
        badge: 'bg-rose-200 text-rose-800'
      }
    };
    return configs[priority] || configs.medium;
  }, []);

  // ================================
  // ⏰ VERIFICAR ATRASO
  // ================================
  const isOverdue = useMemo(() => {
    if (!task?.dueDate || task.status === 'done') return false;
    
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(task.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    } catch {
      return false;
    }
  }, [task?.dueDate, task?.status]);

  // ================================
  // 🔥 V15: CARREGAR DADOS DESDE DATACONTEXT
  // ================================
  useEffect(() => {
    const loadTaskData = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const ctx = getContext();
        let taskData = null;

        // 🔥 V15: Buscar tarea desde DataContext primero
        if (ctx?.collections?.tasks?.findById) {
          taskData = await ctx.collections.tasks.findById(id);
        }
        
        // Fallback a database.js
        if (!taskData && database?.getTaskById) {
          taskData = await database.getTaskById(id);
        }
        
        if (!taskData) {
          setError('Tarefa não encontrada');
          showToast('Tarefa não encontrada', 'error', {
            title: 'Erro'
          });
          setTimeout(() => navigate('/tarefas'), 2000);
          return;
        }

        setTask(taskData);

        // 🔥 V15: Cargar cliente vinculado desde DataContext
        if (taskData.clientId) {
          let clientData = null;
          if (ctx?.collections?.clients?.findById) {
            clientData = await ctx.collections.clients.findById(taskData.clientId);
          }
          if (!clientData && database?.getClientById) {
            clientData = await database.getClientById(taskData.clientId);
          }
          setClient(clientData || null);
        }

        // 🔥 V15: Cargar inmueble vinculado desde DataContext
        if (taskData.propertyId) {
          let propertyData = null;
          if (ctx?.collections?.properties?.findById) {
            propertyData = await ctx.collections.properties.findById(taskData.propertyId);
          }
          if (!propertyData && database?.getPropertyById) {
            propertyData = await database.getPropertyById(taskData.propertyId);
          }
          setProperty(propertyData || null);
        }

      } catch (err) {
        console.error('❌ [V15 TaskDetails] Erro ao carregar tarefa:', err);
        setError('Erro ao carregar dados da tarefa');
        showToast('Não foi possível carregar a tarefa', 'error', {
          title: 'Erro'
        });
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [id, getContext, database, navigate, showToast]);

  // ================================
  // ✨ EMOJIS AUTOMÁTICOS PARA DESCRIÇÃO
  // ================================
  const enhancedDescription = useMemo(() => {
    if (!task?.description) return null;

    let desc = task.description;

    const emojiMap = {
      'ligar|telefonar|call': '📞',
      'reunião|reuniao|meeting': '🤝',
      'email|e-mail': '📧',
      'whatsapp|whats': '💬',
      'documento|documentos|doc': '📄',
      'contrato': '📋',
      'financeiro|pagamento|pagar': '💰',
      'urgente|urgência|urgencia': '⚡',
      'cliente': '👤',
      'imóvel|imovel|casa|apartamento': '🏠',
      'visita|vistoria': '🔍',
      'proposta': '📝',
      'fechado|vendido': '✅',
      'problema|erro|bug': '⚠️',
      'ideia|idea|sugestão': '💡',
      'parabéns|parabens': '🎉',
      'importante|priority': '🔥'
    };

    Object.entries(emojiMap).forEach(([pattern, emoji]) => {
      const regex = new RegExp(`\\b(${pattern})\\b`, 'gi');
      desc = desc.replace(regex, `${emoji} $1`);
    });

    return desc;
  }, [task?.description]);

  // ================================
  // 🎯 HANDLERS
  // ================================
  const handleEdit = useCallback(() => {
    navigate(`/tarefas/editar/${id}`);
  }, [navigate, id]);

  // 🔥 V15: handleComplete con DataContext + SyncManager
  const handleComplete = useCallback(async () => {
    if (!task || task.status === 'done' || processing) return;

    setProcessing(true);
    try {
      const ctx = getContext();

      // 🔥 V15: Actualizar vía DataContext
      if (ctx?.collections?.tasks?.update) {
        await ctx.collections.tasks.update(task.id, {
          status: 'done',
          completedAt: new Date().toISOString(),
          completedBy: authUserId,
          updatedAt: new Date().toISOString()
        });

        // Disparar sync con SyncManager V14
        if (ctx.syncManager) {
          ctx.syncManager.markRecentlyModified(task.id);
          ctx.syncManager.enqueue({
            collection: 'tasks',
            type: 'UPDATE',
            localId: task.id,
            data: { status: 'done', completedAt: new Date().toISOString() },
            timestamp: new Date().toISOString(),
            userId: authUserId
          }).catch(() => {});
        }
      } else if (database?.updateTask) {
        // Fallback a database.js
        await database.updateTask(task.id, {
          ...task,
          status: 'done',
          completedAt: new Date().toISOString(),
          completedBy: database.userId
        });
      }

      showToast('🎉 Tarefa concluída com sucesso!', 'success', {
        title: 'Parabéns!'
      });

      // Recargar tarea actualizada
      let updatedTask = null;
      if (ctx?.collections?.tasks?.findById) {
        updatedTask = await ctx.collections.tasks.findById(id);
      }
      if (!updatedTask && database?.getTaskById) {
        updatedTask = await database.getTaskById(id);
      }
      setTask(updatedTask);

    } catch (error) {
      console.error('❌ [V15 TaskDetails] Erro ao completar tarefa:', error);
      showToast('Erro ao completar tarefa', 'error');
    } finally {
      setProcessing(false);
    }
  }, [task, id, getContext, database, showToast, processing, authUserId]);

  // 🔥 V15: handleDelete con DataContext (tombstone triple-capa)
  const handleDelete = useCallback(async () => {
    if (!task || processing) return;

    setProcessing(true);
    try {
      const ctx = getContext();

      // 🔥 V15: Eliminar vía DataContext → tombstone triple-capa + SyncManager
      if (ctx?.collections?.tasks?.delete) {
        await ctx.collections.tasks.delete(task.id);
      } else if (database?.deleteTask) {
        // Fallback a database.js
        await database.deleteTask(task.id);
      }
      
      showToast('Tarefa excluída com sucesso', 'success', {
        title: '🗑️ Removida'
      });
      
      navigate('/tarefas');
      
    } catch (error) {
      console.error('❌ [V15 TaskDetails] Erro ao excluir tarefa:', error);
      showToast('Erro ao excluir tarefa', 'error');
      setProcessing(false);
      setShowDeleteModal(false);
    }
  }, [task, getContext, database, navigate, showToast, processing]);

  const handleShare = useCallback(async () => {
    if (!task) return;

    const taskUrl = getTaskShareUrl(task.id);
    const shareData = {
      title: `Tarefa: ${task.title}`,
      text: `${task.title}\n\n${task.description || ''}\n\nStatus: ${getStatusConfig(task.status).label}\nPrioridade: ${getPriorityConfig(task.priority).label}`,
      url: taskUrl
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        showToast('Compartilhado com sucesso!', 'success');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Erro ao compartilhar:', err);
        }
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      showToast('Link copiado!', 'success');
    }
    setShowShareMenu(false);
  }, [task, showToast, getStatusConfig, getPriorityConfig]);

  // ================================
  // 💬 COMPARTILHAR WHATSAPP
  // ================================
  const handleWhatsAppShare = useCallback(() => {
    if (!task) return;

    const statusLabel = getStatusConfig(task.status).label;
    const priorityLabel = getPriorityConfig(task.priority).label;
    const taskUrl = getTaskShareUrl(task.id);
    
    const dataCriacao = task.createdAt ? new Date(task.createdAt).toLocaleDateString('pt-BR') : '—';
    const dataPrazo = task.dueDate ? new Date(task.dueDate).toLocaleDateString('pt-BR') : 'Sem prazo';
    
    const propertyLink = property 
      ? `\n🏠 *Imóvel:* ${property.title}\n🔗 *Link do imóvel:* ${window.location.origin}/imovel/${property.id}\n`
      : '';
    
    const mensagem = `
📋 *Tarefa: ${task.title}*

📊 *Status:* ${statusLabel}
⚡ *Prioridade:* ${priorityLabel}
📅 *Criada em:* ${dataCriacao}
⏰ *Prazo:* ${dataPrazo}

📝 *Descrição:*
${task.description || 'Sem descrição'}

${client ? `👤 *Cliente:* ${client.name}` : ''}
${propertyLink}
🔗 *Link da tarefa:* ${taskUrl}

Compartilhado via ${company?.nome || 'IlhaCRM'}
    `.trim();

    const url = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
    
    showToast('WhatsApp aberto com a tarefa', 'success');
    setShowShareMenu(false);
  }, [task, client, property, company, getStatusConfig, getPriorityConfig, showToast]);

  // ================================
  // 📧 COMPARTILHAR EMAIL
  // ================================
  const handleEmailShare = useCallback(() => {
    if (!task) return;

    const statusLabel = getStatusConfig(task.status).label;
    const priorityLabel = getPriorityConfig(task.priority).label;
    const taskUrl = getTaskShareUrl(task.id);
    
    const dataCriacao = task.createdAt ? new Date(task.createdAt).toLocaleDateString('pt-BR') : '—';
    const dataPrazo = task.dueDate ? new Date(task.dueDate).toLocaleDateString('pt-BR') : 'Sem prazo';
    
    const assunto = `📋 Tarefa: ${task.title}`;
    
    const corpo = `
${company?.nome || 'IlhaCRM'} - Detalhes da Tarefa

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 TAREFA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Título: ${task.title}
Status: ${statusLabel}
Prioridade: ${priorityLabel}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📅 DATAS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Criada em: ${dataCriacao}
Prazo: ${dataPrazo}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 DESCRIÇÃO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${task.description || 'Sem descrição'}

${client ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
👤 CLIENTE VINCULADO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Nome: ${client.name}
Email: ${client.email || '—'}
Telefone: ${client.phone || '—'}
` : ''}

${property ? `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🏠 IMÓVEL VINCULADO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Título: ${property.title}
Preço: ${property.price ? `R$ ${Number(property.price).toLocaleString('pt-BR')}` : '—'}
Cidade: ${property.city || '—'}
` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔗 LINK PARA ACESSO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${taskUrl}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📱 Compartilhado via ${company?.nome || 'IlhaCRM'}
    `.trim();

    window.location.href = `mailto:?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
    
    showToast('Email preparado com os dados da tarefa', 'success');
    setShowShareMenu(false);
  }, [task, client, property, company, getStatusConfig, getPriorityConfig, showToast]);

  // ================================
  // 🔗 COPIAR LINK
  // ================================
  const handleCopyLink = useCallback(async () => {
    if (!task) return;

    try {
      const taskUrl = getTaskShareUrl(task.id);
      await navigator.clipboard.writeText(taskUrl);
      showToast('Link copiado!', 'success');
    } catch (err) {
      console.error('Erro ao copiar:', err);
      showToast('Erro ao copiar link', 'error');
    }
    setShowShareMenu(false);
  }, [task, showToast]);

  // ================================
  // 🎨 LOADING SKELETON PREMIUM
  // ================================
  if (loading) {
    return (
      <div className={isMobile ? "p-3 max-w-4xl mx-auto" : "p-4 md:p-6 max-w-4xl mx-auto"}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-muted rounded-xl animate-pulse" />
            <div className="flex-1">
              <div className="h-5 bg-muted rounded w-1/3 animate-pulse" />
              <div className="h-3 bg-muted rounded w-1/4 mt-2 animate-pulse" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-color p-6 space-y-6">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-muted rounded-xl animate-pulse" />
              <div className="flex-1">
                <div className="h-6 bg-muted rounded w-2/3 animate-pulse" />
                <div className="h-4 bg-muted rounded w-1/2 mt-3 animate-pulse" />
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full animate-pulse" />
              <div className="h-4 bg-muted rounded w-5/6 animate-pulse" />
              <div className="h-4 bg-muted rounded w-4/6 animate-pulse" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="h-16 bg-muted rounded-xl animate-pulse" />
              <div className="h-16 bg-muted rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ================================
  // ❌ ERROR STATE
  // ================================
  if (error || !task) {
    return (
      <div className={isMobile ? "p-3 max-w-4xl mx-auto" : "p-4 md:p-6 max-w-4xl mx-auto"}>
        <div className="bg-white rounded-2xl border border-rose-200 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-2xl mb-4">
            <AlertCircle className="w-8 h-8 text-rose-600" />
          </div>
          <h2 className="text-xl font-bold text-primary mb-2">
            {error || 'Tarefa não encontrada'}
          </h2>
          <p className="text-muted mb-6">
            A tarefa que você está procurando pode ter sido excluída ou não existe.
          </p>
          <button
            onClick={() => navigate('/tarefas')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Voltar para tarefas
          </button>
        </div>
      </div>
    );
  }

  const StatusConfig = getStatusConfig(task.status);
  const PriorityConfig = getPriorityConfig(task.priority);
  const StatusIcon = StatusConfig.icon;
  const PriorityIcon = PriorityConfig.icon;

  // ================================
  // 🖼️ RENDER PRINCIPAL - ULTRA PROFISSIONAL (IDÉNTICO AL ORIGINAL)
  // ================================
  return (
    <div className={isMobile ? "p-3 max-w-4xl mx-auto" : "p-4 md:p-6 max-w-4xl mx-auto"}>
      
      {/* HEADER COM NAVEGAÇÃO */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <button
          onClick={() => navigate('/tarefas')}
          className="flex items-center gap-2 text-secondary hover:text-primary transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className={isMobile ? 'text-sm' : ''}>Voltar</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={handleEdit}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            title="Editar tarefa"
          >
            <Edit3 size={isMobile ? 18 : 20} className="text-secondary" />
          </button>

          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              title="Compartilhar"
            >
              <Share2 size={isMobile ? 18 : 20} className="text-secondary" />
            </button>

            <AnimatePresence>
              {showShareMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-color overflow-hidden z-50"
                >
                  <button
                    onClick={handleWhatsAppShare}
                    className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-3 transition-colors"
                  >
                    <MessageCircle size={16} className="text-emerald-600" />
                    <span className="text-sm">Compartilhar no WhatsApp</span>
                  </button>

                  <button
                    onClick={handleEmailShare}
                    className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-3 transition-colors border-t border-slate-100"
                  >
                    <Mail size={16} className="text-blue-600" />
                    <span className="text-sm">Enviar por E-mail</span>
                  </button>

                  <button
                    onClick={handleCopyLink}
                    className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-3 transition-colors border-t border-slate-100"
                  >
                    <FileText size={16} className="text-muted" />
                    <span className="text-sm">Copiar link</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* CARD PRINCIPAL */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-color shadow-sm overflow-hidden"
      >
        <div className={`${StatusConfig.bgLight} px-6 py-4 border-b ${StatusConfig.border}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${StatusConfig.color.split(' ')[0]}`}>
                <StatusIcon className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h1 className={`font-bold text-xl ${StatusConfig.textDark}`}>
                  {task.title}
                </h1>
                <p className="text-sm text-muted mt-0.5">
                  Criada em {formatDate(task.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${PriorityConfig.badge}`}>
                {PriorityConfig.label}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${StatusConfig.color}`}>
                {StatusConfig.label}
              </span>
            </div>
          </div>
        </div>

        <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
          
          {/* TIMELINE */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted" />
              <span className="text-secondary">Criada:</span>
              <span className="font-medium text-primary">
                {formatDate(task.createdAt, 'full')}
              </span>
            </div>

            {task.dueDate && (
              <div className={`flex items-center gap-2 text-sm ${
                isOverdue ? 'text-rose-600' : 'text-secondary'
              }`}>
                <Calendar className="w-4 h-4" />
                <span>Prazo:</span>
                <span className={`font-medium ${isOverdue ? 'text-rose-700' : 'text-primary'}`}>
                  {formatDate(task.dueDate, 'relative')}
                  {isOverdue && ' ⚠️'}
                </span>
              </div>
            )}

            {task.completedAt && (
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Concluída:</span>
                <span className="font-medium text-emerald-700">
                  {formatDate(task.completedAt, 'full')}
                </span>
              </div>
            )}
          </div>

          {/* DESCRIÇÃO ENRIQUECIDA */}
          {enhancedDescription ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="prose prose-slate max-w-none"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-purple-100 rounded-lg">
                  <Sparkles size={16} className="text-purple-600" />
                </div>
                <h3 className="text-sm font-semibold text-secondary">Descrição</h3>
              </div>
              <div className="bg-muted rounded-xl p-4 border border-color">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ node, ...props }) => (
                      <p className="text-secondary leading-relaxed mb-2" {...props} />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-primary" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic text-secondary" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul className="list-disc list-inside space-y-1 mb-2" {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol className="list-decimal list-inside space-y-1 mb-2" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="text-secondary" {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a className="text-blue-600 hover:underline" target="_blank" {...props} />
                    ),
                  }}
                >
                  {enhancedDescription}
                </ReactMarkdown>
              </div>
            </motion.div>
          ) : task.description ? (
            <div className="bg-muted rounded-xl p-4 border border-color">
              <p className="text-secondary whitespace-pre-wrap leading-relaxed">
                {task.description}
              </p>
            </div>
          ) : null}

          {/* CLIENTE VINCULADO */}
          {client && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="group"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-blue-100 rounded-lg">
                  <User size={16} className="text-blue-600" />
                </div>
                <h3 className="text-sm font-semibold text-secondary">Cliente Vinculado</h3>
              </div>
              
              <div
                onClick={() => navigate(`/clientes/${client.id}`)}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 cursor-pointer hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                    {client.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-primary group-hover:text-blue-600 transition-colors">
                      {client.name}
                    </h4>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      {client.email && (
                        <span className="flex items-center gap-1 text-secondary">
                          <Mail size={12} />
                          {client.email}
                        </span>
                      )}
                      {client.phone && (
                        <span className="flex items-center gap-1 text-secondary">
                          <Phone size={12} />
                          {client.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-muted group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            </motion.div>
          )}

          {/* IMÓVEL VINCULADO */}
          {property && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="group"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-emerald-100 rounded-lg">
                  <Home size={16} className="text-emerald-600" />
                </div>
                <h3 className="text-sm font-semibold text-secondary">Imóvel Vinculado</h3>
              </div>

              <div
                onClick={() => navigate(`/admin/imoveis/${property.id}`)}
                className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200 cursor-pointer hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-4">
                  <PropertyImage property={property} size="md" />

                  <div className="flex-1">
                    <h4 className="font-semibold text-primary group-hover:text-emerald-600 transition-colors">
                      {property.title}
                    </h4>
                    <div className="flex items-center gap-3 mt-1 text-sm">
                      {property.price && (
                        <span className="flex items-center gap-1 text-emerald-600 font-medium">
                          <DollarSign size={12} />
                          {Number(property.price).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL'
                          })}
                        </span>
                      )}
                      {property.city && (
                        <span className="flex items-center gap-1 text-secondary">
                          <MapPin size={12} />
                          {property.city}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-muted group-hover:text-emerald-500 transition-colors" />
                </div>
              </div>
            </motion.div>
          )}

          {/* TAGS */}
          {task.tags && task.tags.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-amber-100 rounded-lg">
                  <Tag size={16} className="text-amber-600" />
                </div>
                <h3 className="text-sm font-semibold text-secondary">Tags</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {task.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-muted text-secondary rounded-full text-xs font-medium border border-color"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* ANEXOS */}
          {task.attachments && task.attachments.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 bg-rose-100 rounded-lg">
                  <Paperclip size={16} className="text-rose-600" />
                </div>
                <h3 className="text-sm font-semibold text-secondary">Anexos</h3>
              </div>
              <div className="space-y-2">
                {task.attachments.map((att, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 bg-muted rounded-xl border border-color hover:bg-muted transition-colors cursor-pointer"
                  >
                    <FileText size={16} className="text-muted" />
                    <span className="text-sm text-secondary flex-1">{att.name || 'Arquivo'}</span>
                    <Download size={14} className="text-muted" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* AÇÕES FINAIS */}
        <div className={`${isMobile ? 'p-4' : 'p-6'} bg-muted border-t border-color flex justify-end gap-3`}>
          {task.status !== 'done' && (
            <button
              onClick={handleComplete}
              disabled={processing}
              className={`flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                isMobile ? 'flex-1 justify-center' : ''
              }`}
            >
              {processing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  Concluir Tarefa
                </>
              )}
            </button>
          )}

          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={processing}
            className={`flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
              isMobile ? 'flex-1 justify-center' : ''
            }`}
          >
            <Trash2 size={18} />
            {!isMobile && 'Excluir'}
          </button>
        </div>
      </motion.div>

      {/* MODAL DE EXCLUSÃO — NativeSheet */}
      <NativeSheet
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir Tarefa"
        tone="danger"
        footer={
          <div className={`flex gap-2 ${isMobile ? "flex-col-reverse" : "justify-end"}`}>
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className={`rounded-xl font-medium border border-color text-secondary hover:bg-hover transition active:scale-[0.98] ${
                isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={processing}
              className={`rounded-xl font-semibold text-white bg-rose-600 hover:bg-rose-500 shadow-md transition active:scale-[0.98] disabled:opacity-60 ${
                isMobile ? "w-full py-3 text-sm" : "px-4 py-2.5 text-sm"
              }`}
            >
              {processing ? "Excluindo..." : "Excluir"}
            </button>
          </div>
        }
      >
        <div className="flex gap-3 items-start">
          <div className="w-11 h-11 rounded-2xl bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-secondary mb-2">Esta ação não pode ser desfeita.</p>
            <div className="bg-muted p-3 rounded-xl border border-color">
              <p className="font-semibold text-primary text-sm">{task?.title}</p>
              {task?.description && (
                <p className="text-xs text-muted mt-1 line-clamp-2">{task.description}</p>
              )}
            </div>
          </div>
        </div>
      </NativeSheet>
    </div>
  );
}